import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { translations } from "../../../configs/translations"
import { Vehicle } from "../../../models/Vehicle"
import RaceService from "../../../services/RaceService"
import { useStore } from "../../../store/store"
import { SearchRace } from "../../main/searchRace/SearchRace"
import { FreeroamRoulette } from "../freeroamRoulette/FreeroamRoulette"
import { FreeroamInvite } from "./FreeroamInvite"
import "./FreeroamInvite.css"

const MAX_INVITES = 3
const INVITE_DISPLAY_TIME = 30_000

export interface Invite {
    id: number
    text: string
    raceType: string
    rank: number
    carType: string
    isRanked: boolean
    isLeaving?: boolean
}

export interface InviteData {
    raceType: string
    rank: number
    carType: string
    isRanked: boolean
}

interface Props {
    incomingInvite: InviteData | null
}

let nextId = 0

export const FreeroamInviteList = ({ incomingInvite }: Props) => {
    const [activeInvites, setActiveInvites] = useState<Invite[]>([])
    const [queue, setQueue] = useState<Invite[]>([])

    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
    const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null)
    const [showSearchRace, setShowSearchRace] = useState(true)

    const createInvite = (
        text: string,
        raceType: string,
        rank: number,
        carType: string,
        isRanked: boolean
    ): Invite => {
        return {
            id: nextId++,
            text,
            raceType,
            rank,
            carType,
            isRanked
        }
    }

    useEffect(() => {
        if (!incomingInvite) return

        const invite = createInvite(
            t.race_invite,
            incomingInvite.raceType,
            incomingInvite.rank,
            incomingInvite.carType?.toLowerCase(),
            incomingInvite.isRanked
        )

        setQueue(prev => [...prev, invite])
    }, [incomingInvite, t])

    useEffect(() => {
        if (queue.length === 0) return
        if (activeInvites.length >= MAX_INVITES) return

        const [next, ...rest] = queue
        setActiveInvites(prev => [next, ...prev])
        setQueue(rest)
    }, [queue, activeInvites.length])

    useEffect(() => {
        if (activeInvites.length === 0) return

        const leaveTimer = setTimeout(() => {
            setActiveInvites(prev => {
                if (prev.length === 0) return prev
                const copy = [...prev]
                const topIndex = copy.length - 1
                copy[topIndex] = { ...copy[topIndex], isLeaving: true }
                return copy
            })
        }, INVITE_DISPLAY_TIME)

        return () => clearTimeout(leaveTimer)
    }, [activeInvites.length])

    useEffect(() => {
        if (selectedInvite && selectedVehicle) {
            if (selectedInvite.isRanked && selectedInvite.raceType === "race") {
                RaceService.addPlayerToSearchRankedRace(selectedVehicle.id)
                    .then(() => {
                        setShowSearchRace(true)
                        setSelectedInvite(null)
                        setSelectedVehicle(null)
                    })
                    .catch((error) => {
                        toast.error(
                            error?.response?.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: "notification-error" }
                        )
                    })
            } else if (!selectedInvite.isRanked && selectedInvite.raceType === "race") {
                RaceService.addPlayerToSearchUnRankedRace(selectedVehicle.id)
                    .then(() => {
                        setShowSearchRace(true)
                        setSelectedInvite(null)
                        setSelectedVehicle(null)
                    })
                    .catch((error) => {
                        toast.error(
                            error?.response?.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: "notification-error" }
                        )
                    })
            }
        }
    }, [selectedInvite, selectedVehicle, t])

    return (
        <>
            {showSearchRace && (
                <SearchRace
                    selectedVehicle={selectedVehicle}
                    withButton={["cancel"]}
                    setShow={() => {
                        setShowSearchRace(false)
                        setSelectedVehicle(null)
                        setSelectedInvite(null)
                    }}
                />
            )}

            <div className="freeroam-invite-list">
                {activeInvites.map((invite, index) => (
                    <FreeroamInvite
                        key={invite.id}
                        id={invite.id}
                        text={invite.text}
                        raceType={invite.raceType}
                        rank={invite.rank}
                        carType={invite.carType}
                        isLeaving={invite.isLeaving}
                        countingDown={index === activeInvites.length - 1}
                        countdownKey={activeInvites.length}
                        countdownMs={INVITE_DISPLAY_TIME}
                        onAnimationEnd={(index) => {
                            setActiveInvites(prev => prev.filter((_, i) => i !== index))
                        }}
                        onAccept={() => {
                            setSelectedInvite(invite)
                        }}
                    />
                ))}
            </div>

            {selectedInvite && (
                <FreeroamRoulette
                    setSelectedVehicle={setSelectedVehicle}
                    invite={selectedInvite}
                />
            )}
        </>
    )
}